import { useEffect, useState } from 'react'
import { Package, ArrowRight } from 'lucide-react'
import { fetchCategories } from '../data/api'
import { SkeletonGrid } from '../components/SkeletonGrid'

const CATEGORY_STYLES = {
  'Edible Oils': { glow: '#E58B25', gradient: 'from-amber-500/20 to-orange-500/20', text: '#9A4E11' },
  'Grains & Cereals': { glow: '#D4A418', gradient: 'from-yellow-500/20 to-amber-500/20', text: '#7A5C0F' },
  'Fertilizers': { glow: '#4A7C59', gradient: 'from-emerald-500/20 to-green-600/20', text: '#2C5B3A' },
  'Beverages': { glow: '#7A4F9E', gradient: 'from-violet-500/20 to-purple-600/20', text: '#532B74' },
  'Sweeteners': { glow: '#B85C5C', gradient: 'from-rose-500/20 to-red-500/20', text: '#7A2D2D' }
}

const DEFAULT_STYLE = { glow: '#D4A418', gradient: 'from-brand-gold/15 to-brand-gold/5', text: '#7A5C0F' }

function getStyle(categoryName) {
  return CATEGORY_STYLES[categoryName] || DEFAULT_STYLE
}

export function CategoriesView({ segment, onNavigate, onSelectCategory }) {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchCategories(segment)
      .then((res) => {
        if (cancelled) return
        if (res.success) setCategories(res.data)
      })
      .catch((err) => {
        if (!cancelled) console.error('Categories load failed:', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [segment])

  const handleClick = (categoryName) => {
    onSelectCategory(categoryName)
    onNavigate('catalog')
  }

  return (
    <main>
      <section className="relative h-80 bg-brand-navy px-4 sm:px-6 lg:px-8">
        <img
          src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?auto=format&fit=crop&w=1600&q=80"
          alt="Shipping containers at port"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/90 to-brand-navy/40" />
        <div className="relative mx-auto flex h-full max-w-7xl flex-col items-start justify-center text-white">
          <span className="mb-3 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-gold-light">
            Product Lines
          </span>
          <h1 className="font-display text-4xl font-bold text-white text-shadow sm:text-5xl">Categories</h1>
          <p className="mt-3 max-w-xl text-white/80">Browse by product family and filter the catalog instantly.</p>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          {loading ? (
            <SkeletonGrid count={6} />
          ) : categories.length === 0 ? (
            <p className="py-16 text-center text-brand-slate">No categories found. Please check back later.</p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((cat) => {
                const style = getStyle(cat.name)
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleClick(cat.name)}
                    style={{ '--glow-color': style.glow }}
                    className="glow-card group relative overflow-hidden rounded-2xl border border-white/60 bg-white p-8 text-left shadow-card"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${style.gradient} opacity-60`} />
                    <div className="relative z-10 flex h-full flex-col">
                      <div
                        className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl shadow-sm"
                        style={{ backgroundColor: style.glow }}
                      >
                        <Package className="h-7 w-7 text-white" />
                      </div>
                      <h3 className="font-display text-2xl font-bold text-brand-ink" style={{ color: style.text }}>
                        {cat.name}
                      </h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-brand-slate">{cat.description || 'Explore products in this category.'}</p>
                      <div className="mt-6 flex items-center gap-2 text-sm font-semibold" style={{ color: style.text }}>
                        View products
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 rounded-2xl bg-brand-navy p-10 text-center text-white sm:flex-row sm:text-left">
          <img
            src="https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=400&q=80"
            alt="Warehouse sourcing"
            loading="lazy"
            className="h-28 w-28 shrink-0 rounded-2xl object-cover"
          />
          <div className="flex-1">
            <h3 className="font-display text-xl font-bold">Can't find what you need?</h3>
            <p className="mt-2 max-w-xl text-sm text-white/70">
              At Alpha Import Export, we source beyond the catalog. Send us your product specification, volumes and
              destination — our procurement team will find it.
            </p>
          </div>
          <button
            onClick={() => onNavigate('contact')}
            className="shrink-0 rounded-xl bg-brand-gold px-6 py-3 text-sm font-bold text-brand-navy hover:bg-brand-gold-light"
          >
            Request sourcing
          </button>
        </div>
      </section>
    </main>
  )
}
